import { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { validarRut, formatearRut } from '../../../../utilities/RutUtil';
import { publicarCliente, getClienteRescatar } from '../../../../service/AffariService';

const ClienteDialog = ({ visible, onHide, dropdownData }) => {
    const [razonSocial, setRazonSocial] = useState('');
    const [correo, setCorreo] = useState('');
    const [correoValido, setCorreoValido] = useState(true);
    const [telefono, setTelefono] = useState('');
    const [contacto, setContacto] = useState('');
    const [almacen, setAlmacen] = useState(null);
    const [giro, setGiro] = useState(null);
    const [sector, setSector] = useState(null);
    const [direccion, setDireccion] = useState('');
    const [comuna, setComuna] = useState(null);
    const [ciudad, setCiudad] = useState('');
    const [rut, setRut] = useState('');
    const [rutValido, setRutValido] = useState(true);
    const toast = useRef(null);

    const resetForm = () => {
        setRazonSocial('');
        setCorreo('');
        setCorreoValido(true);
        setTelefono('');
        setContacto('');
        setAlmacen(null);
        setGiro(null);
        setSector(null);
        setDireccion('');
        setComuna(null);
        setCiudad('');
        setRut('');
        setRutValido(true);
    };

    const handleHide = () => {
        resetForm();
        if (onHide) {
            onHide();
        }
    };

    const formatChileanPhone = (value) => {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length <= 8) {
            return cleaned.replace(/(\d{4})(\d{0,4})/, '$1 $2').trim();
        }
        return cleaned.replace(/(\d{2})(\d{4})(\d{0,4})/, '+$1 $2 $3').trim();
    };

    const handleTelefonoChange = (e) => {
        const formatted = formatChileanPhone(e.target.value);
        setTelefono(formatted);
    };

    const handleRutChange = (e) => {
        let val = e.target.value;
        val = formatearRut(val);
        setRut(val);
        const esValido = validarRut(val);
        setRutValido(esValido);

        if (!esValido) {
            return;
        }

        getClienteRescatar(val)
            .then((data) => {
                if (!data || data.ERROR) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: data?.ERROR || 'No se encontró información para el RUT', life: 3000 });
                    return;
                }

                setRazonSocial(data.razonSocial ?? '');
                setContacto(data.contacto?.nombre ?? '');
                const email = data.contacto?.email ?? '';
                setCorreo(email);
                setCorreoValido(validarCorreo(email));
                setTelefono(formatChileanPhone(data.contacto?.telefono ?? ''));
                setDireccion(data.direccion ?? '');

                const giroSeleccionado = dropdownData?.giros?.find((item) => item.codigo === data.giro?.codigo) ?? null;
                const sectorSeleccionado = dropdownData?.sectores?.find((item) => item.codigo === data.sector?.codigo) ?? null;
                const comunaSeleccionada = dropdownData?.comunas?.find((item) => item.codigo === data.comuna?.codigo) ?? null;

                setGiro(giroSeleccionado);
                setSector(sectorSeleccionado);
                setComuna(comunaSeleccionada);

                if (comunaSeleccionada?.ciudad?.descripcion) {
                    setCiudad(comunaSeleccionada.ciudad.descripcion);
                } else {
                    setCiudad(data.ciudad?.descripcion ?? '');
                }
            })
            .catch((error) => {
                console.error('Error al rescatar cliente:', error);
                //toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo rescatar el cliente', life: 3000 });
            });
    };

    const validarCorreo = (value) => {
        if (!value) return true;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
    };

    const handleCorreoChange = (e) => {
        const value = e.target.value;
        setCorreo(value);
        setCorreoValido(validarCorreo(value));
    };

    const [loading, setLoading] = useState(false);

    const handleGuardar = () => {
        setLoading(true);
        
        if (!rutValido || !rut) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'RUT inválido o vacío', life: 3000 });
            setLoading(false);
            return;
        }

        if (!validarCorreo(correo)) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Correo electrónico inválido', life: 3000 });
            setLoading(false);
            return;
        }

        const cliente = {
            almacen: almacen?.almacen || '',
            rut: rut,
            razon_social: razonSocial,
            giro: giro?.codigo || '',
            sector: sector?.codigo || '',
            direccion: direccion,
            comuna: comuna?.codigo || '',
            nombre: contacto,
            telefono: telefono.replace(/\D/g, ''),
            correo: correo,
            condicion_pago: "25"
        };

        publicarCliente(cliente).then(() => {
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Cliente creado con éxito', life: 3000 });
            setTimeout(() => {
                onHide();
            }, 1000);
        }).catch(error => {
            console.error("Error al crear cliente:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al crear cliente', life: 3000 });
        }).finally(() => {
            setLoading(false);
        });

    };

    const onComunaChange = (e) => {
        setComuna(e.value);
        if (e.value && e.value.ciudad) {
            setCiudad(e.value.ciudad.descripcion);
        } else {
            setCiudad('');
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog 
                header="Nuevo Cliente" 
                visible={visible} 
                className="custom-dialog"
                onHide={handleHide}
                footer={
                    <div className="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
                        <Button label="Cancelar" icon="pi pi-times" onClick={handleHide} className="p-button-text" disabled={loading} />
                        <Button label="Guardar" icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"} onClick={handleGuardar} disabled={loading} />
                    </div>
                }
            >
        
            <div className="formgrid grid p-fluid">

                {/* Identificación */}
                <div className="col-12 mb-3">
                    <div className="text-500 font-medium mb-2">Identificación</div>
                    <div className="formgrid grid">
                        <div className="field col-12 md:col-6">
                            <label htmlFor="rut" className="font-medium">RUT</label>
                            <InputText 
                                id="rut" 
                                value={rut} 
                                onChange={handleRutChange} 
                                className={!rutValido ? 'p-invalid' : ''}
                                placeholder="Ej: 12.345.678-9"
                            />
                            {!rutValido && <small className="p-error block">RUT inválido</small>}
                        </div>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="razonSocial" className="font-medium">Razón Social</label>
                            <InputText 
                                id="razonSocial" 
                                value={razonSocial} 
                                onChange={(e) => setRazonSocial(e.target.value)} 
                                placeholder="Nombre de la razón social"
                            />
                        </div>
                    </div>
                </div>

                {/* Clasificación */}
                <div className="col-12 mb-3">
                    <div className="text-500 font-medium mb-2">Clasificación</div>
                    <div className="formgrid grid">
                        <div className="field col-12 md:col-4">
                            <label htmlFor="centro" className="font-medium">Centro</label>
                            <Dropdown
                                id="centro"
                                value={almacen}
                                options={dropdownData?.almacens || []}
                                onChange={(e) => setAlmacen(e.value)}
                                optionLabel="sucursal"
                                placeholder="Seleccione centro"
                                filter
                            />
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="giro" className="font-medium">Giro</label>
                            <Dropdown
                                id="giro"
                                value={giro}
                                options={dropdownData?.giros || []}
                                onChange={(e) => setGiro(e.value)}
                                optionLabel="descripcion"
                                placeholder="Seleccione giro"
                                filter
                            />
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="sector" className="font-medium">Sector</label>
                            <Dropdown
                                id="sector"
                                value={sector}
                                options={dropdownData?.sectores || []}
                                onChange={(e) => setSector(e.value)}
                                optionLabel="descripcion"
                                placeholder="Seleccione sector"
                                filter
                            />
                        </div>
                    </div>
                </div>

                {/* Ubicación */}
                <div className="col-12 mb-3">
                    <div className="text-500 font-medium mb-2">Ubicación</div>
                    <div className="formgrid grid">
                        <div className="field col-12">
                            <label htmlFor="direccion" className="font-medium">Dirección</label>
                            <InputText 
                                id="direccion" 
                                value={direccion} 
                                onChange={(e) => setDireccion(e.target.value)} 
                                placeholder="Calle, número, oficina..."
                            />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="comuna" className="font-medium">Comuna</label>
                            <Dropdown
                                id="comuna"
                                value={comuna}
                                options={dropdownData?.comunas || []}
                                onChange={onComunaChange}
                                optionLabel="descripcion"
                                placeholder="Seleccione comuna"
                                filter
                            />
                        </div>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="ciudad" className="font-medium">Ciudad</label>
                            <InputText 
                                id="ciudad" 
                                value={ciudad} 
                                readOnly
                                className="surface-100"
                                placeholder="Automático"
                            />
                        </div>
                    </div>
                </div>

                {/* Contacto */}
                <div className="col-12">
                    <div className="text-500 font-medium mb-2">Contacto</div>
                    <div className="formgrid grid">
                        <div className="field col-12 md:col-4">
                            <label htmlFor="contacto" className="font-medium">Nombre Contacto</label>
                            <InputText 
                                id="contacto" 
                                value={contacto} 
                                onChange={(e) => setContacto(e.target.value)} 
                                placeholder="Nombre completo"
                            />
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="telefono" className="font-medium">Teléfono</label>
                            <InputText 
                                id="telefono" 
                                value={telefono} 
                                onChange={handleTelefonoChange} 
                                placeholder="+56 9 ..."
                                maxLength={12}
                            />
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="correo" className="font-medium">Correo Electrónico</label>
                            <InputText 
                                id="correo" 
                                value={correo} 
                                onChange={handleCorreoChange} 
                                className={!correoValido ? 'p-invalid' : ''}
                                placeholder="nombre@empresa.cl"
                            />
                            {!correoValido && <small className="p-error block">Correo inválido</small>}
                        </div>
                    </div>
                </div>

            </div>
        </Dialog>
        </>
    );
};

export default ClienteDialog;
